import React, { useEffect, useRef, useState } from "react";
import { Menu, ChevronRight } from "lucide-react";

/**
 * UserMenu — generic profile dropdown reused across Client / Driver /
 * Hotel dashboards. Consolidates the small round-button clutter that
 * used to sit in each dashboard's top-bar (Aide, Parrainage, Historique,
 * Documents, Déconnexion, etc.) into one clean menu with a profile
 * header + labelled entries + subtle nudge indicator.
 *
 * Props:
 *   avatarText   - Short label rendered in the round avatar chip (e.g. "MT")
 *   title        - Bold line in the header ("Menu Test" / "John's Hotel")
 *   subtitle     - Muted line below (email or role hint)
 *   items        - Array of { icon, label, hint?, danger?, accent?, onClick, testId }
 *                  `accent`  → the entry is highlighted primary (attract attention)
 *                  `danger`  → the entry is styled destructive (e.g. Déconnexion)
 *   badgeDot     - Boolean : render a red dot on the closed menu button
 *                  → discoverability signal ("something needs your attention").
 *   testId       - Base test id for the anchor button (default "user-menu-btn")
 */
export default function UserMenu({
  avatarText = "S",
  title,
  subtitle,
  items = [],
  badgeDot = false,
  testId = "user-menu-btn",
  panelTestId = "user-menu-panel",
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  // Click-outside to dismiss. `capture: true` so we intercept before any
  // child onClick that would immediately re-open the menu.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler, true);
    document.addEventListener("touchstart", handler, true);
    return () => {
      document.removeEventListener("mousedown", handler, true);
      document.removeEventListener("touchstart", handler, true);
    };
  }, [open]);

  return (
    <div ref={anchorRef} className="relative pointer-events-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ touchAction: "manipulation" }}
        className="relative grid h-9 w-9 flex-none place-items-center rounded-full border border-neutral-200 bg-white shadow-[0_2px_10px_-2px_rgba(0,0,0,0.12)] transition-transform active:scale-95"
        data-testid={testId}
        aria-label="Menu profil"
        aria-expanded={open}
      >
        <Menu className="h-4 w-4 text-primary" />
        {badgeDot && !open && (
          <span
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[hsl(0_84%_60%)] ring-2 ring-white"
            aria-label="Action requise"
          />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[260px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_20px_50px_-15px_rgba(15,38,52,0.35)] z-50"
          data-testid={panelTestId}
        >
          {(title || subtitle) && (
            <div className="flex items-center gap-3 border-b border-neutral-100 bg-gradient-to-br from-primary/5 to-transparent px-4 py-3">
              <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {avatarText}
              </span>
              <div className="min-w-0 flex-1">
                {title && (
                  <p className="truncate font-[Outfit] text-sm font-semibold text-foreground">
                    {title}
                  </p>
                )}
                {subtitle && (
                  <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>
                )}
              </div>
            </div>
          )}

          <ul className="grid gap-0.5 p-1.5">
            {items.map((it, i) => (
              <li key={it.testId || i}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    it.onClick?.();
                  }}
                  className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                    it.danger ? "hover:bg-destructive/10" : "hover:bg-primary/5"
                  }`}
                  data-testid={it.testId}
                >
                  <span
                    className={`grid h-8 w-8 flex-none place-items-center rounded-lg ${
                      it.danger
                        ? "bg-destructive/10 text-destructive"
                        : it.accent
                        ? "bg-primary text-primary-foreground"
                        : "bg-neutral-100 text-primary"
                    }`}
                  >
                    {it.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-sm font-medium ${
                        it.danger ? "text-destructive" : "text-foreground"
                      }`}
                    >
                      {it.label}
                    </p>
                    {it.hint && (
                      <p
                        className={`truncate text-[11px] ${
                          it.accent ? "text-primary font-medium" : "text-muted-foreground"
                        }`}
                      >
                        {it.hint}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 flex-none text-muted-foreground/60 transition group-hover:translate-x-0.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
